import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AiCapability, ArtifactMeta, Citation, DeepAnalysisJob, Transcript } from "@backstory/protocol";
import { ApiRequestError } from "../../api";
import { AI_UNCONFIGURED, aiApi, aiKeys, type DeepResult } from "./api";

// ---------------------------------------------------------------------------
// Availability
// ---------------------------------------------------------------------------

/**
 * Whether this deployment can run AI at all. Every AI control asks, so the
 * answer is cached for the session and shared: one request serves a page full of
 * buttons. An unreachable capability endpoint is treated as available, because a
 * whole product surface should not disappear over one failed GET; the server
 * rejects the work itself if it really cannot run.
 */
export function useAiCapability() {
  const q = useQuery({
    queryKey: aiKeys.capability(),
    queryFn: () => aiApi.capability(),
    staleTime: 60_000,
    retry: false,
  });
  const cap: AiCapability | undefined = q.data;
  const available = cap ? cap.available : true;
  // Automatic fetches wait for the answer; controls do not, so a page full of
  // buttons does not flicker disabled on every load.
  const settled = !q.isPending;
  return useMemo(
    () => ({
      available,
      /** True once the server has answered, either way. Gate background fetches on it. */
      settled,
      /** True only when a capability answer arrived, for tests and spinners. */
      known: !!cap,
      reason: cap?.reason,
      /** False only once the server says this workspace is out of AI credits. */
      creditsAvailable: cap?.creditsAvailable !== false,
      /** Tooltip for a control this gate disables. Empty when AI is available. */
      hint: available ? "" : unavailableHint(cap),
      /** What to do about it, for the popover on a disabled control. */
      detail: available ? "" : unavailableDetail(cap),
      /** `true` while AI cannot run, for `disabled` props. */
      blocked: !available,
      /** The control's own tooltip while AI works, the reason while it does not. */
      titleFor: (own?: string) => (available ? own : unavailableHint(cap)),
    }),
    [available, settled, cap, cap?.creditsAvailable],
  );
}

export type AiCapabilityState = ReturnType<typeof useAiCapability>;

function unavailableHint(cap: AiCapability | undefined): string {
  switch (cap?.reason) {
    case "ai_disabled":
      return "AI is turned off for this workspace";
    case "phi_provider_unavailable":
      return "AI is unavailable for workspaces handling PHI";
    case "role_forbidden":
      return "Your role can read AI output but not run AI";
    case "organization_routing_unconfigured":
      return "AI routing isn't finished for this workspace";
    default:
      return AI_UNCONFIGURED;
  }
}

/** The sentence under the reason: who can change it, and where. */
function unavailableDetail(cap: AiCapability | undefined): string {
  switch (cap?.reason) {
    case "ai_disabled":
      return "An owner or admin can turn it back on in AI settings.";
    case "phi_provider_unavailable":
      return "No provider approved for protected health information is available right now.";
    case "organization_routing_unconfigured":
      return "This workspace is set to use its own providers, but its model routing is incomplete. An owner can finish it in AI settings.";
    case "role_forbidden":
      return "Read-only members do not spend AI credits. Ask an owner or admin for the Support role or above.";
    default:
      return "Nobody has connected an AI provider to this deployment yet.";
  }
}

// ---------------------------------------------------------------------------
// Deep analysis state machine (PLAN §22.7: preview before spend, 200 vs 202)
// ---------------------------------------------------------------------------

export type DeepState<T> =
  | { phase: "idle" }
  | { phase: "starting" }
  | { phase: "running"; job: DeepAnalysisJob; polls: number }
  | { phase: "done"; artifact: T; job?: DeepAnalysisJob; cached: boolean }
  | { phase: "rejected"; job: DeepAnalysisJob }
  | { phase: "failed"; error: string; job?: DeepAnalysisJob };

export interface UseDeepAnalysis<T> {
  state: DeepState<T>;
  start: () => Promise<void>;
  reset: () => void;
  busy: boolean;
}

const POLL_MS = 1500;
const MAX_POLLS = 200; // 5 minutes

/**
 * Drives a metered deep analysis: run → (artifact | job) → poll → artifact.
 * `run` is the API call; `load` fetches the artifact when the job is done
 * (defaults to GET job.resultUrl).
 */
export function useDeepAnalysis<T>(run: () => Promise<DeepResult<T>>, load?: (job: DeepAnalysisJob) => Promise<T>): UseDeepAnalysis<T> {
  const [state, setState] = useState<DeepState<T>>({ phase: "idle" });
  const alive = useRef(true);
  // Claimed on mount, not merely released on unmount: StrictMode mounts,
  // unmounts, and remounts in development, so a flag that is only ever cleared
  // stays cleared and every result after that is dropped on the floor.
  useEffect(() => {
    alive.current = true;
    return () => void (alive.current = false);
  }, []);

  const poll = useCallback(
    async (job: DeepAnalysisJob) => {
      let current = job;
      for (let i = 0; i < MAX_POLLS && alive.current; i++) {
        if (current.status === "done") {
          try {
            const artifact = load ? await load(current) : await aiApi.result<T>(current.resultUrl ?? `/v1/ai/jobs/${encodeURIComponent(current.id)}/result`);
            if (alive.current) setState({ phase: "done", artifact, job: current, cached: false });
          } catch (e) {
            if (alive.current) setState({ phase: "failed", error: (e as Error).message, job: current });
          }
          return;
        }
        if (current.status === "rejected") return void (alive.current && setState({ phase: "rejected", job: current }));
        if (current.status === "failed") return void (alive.current && setState({ phase: "failed", error: current.error ?? "analysis failed", job: current }));
        if (alive.current) setState({ phase: "running", job: current, polls: i + 1 });
        await new Promise((r) => setTimeout(r, POLL_MS));
        try {
          current = await aiApi.job(current.id);
        } catch (e) {
          if (alive.current) setState({ phase: "failed", error: (e as Error).message, job: current });
          return;
        }
      }
      if (alive.current) setState({ phase: "failed", error: "timed out waiting for the analysis", job: current });
    },
    [load],
  );

  const start = useCallback(async () => {
    setState({ phase: "starting" });
    try {
      const r = await run();
      if (!alive.current) return;
      if (r.kind === "artifact") setState({ phase: "done", artifact: r.artifact, cached: true });
      else await poll(r.job);
    } catch (e) {
      if (!alive.current) return;
      const err = e as ApiRequestError;
      setState({ phase: "failed", error: err.status === 402 ? "This organization has no AI allotment left." : err.message });
    }
  }, [run, poll]);

  const reset = useCallback(() => setState({ phase: "idle" }), []);
  const busy = state.phase === "starting" || state.phase === "running";
  return { state, start, reset, busy };
}

// ---------------------------------------------------------------------------
// Transcript + citation resolution
// ---------------------------------------------------------------------------

export function useTranscript(sessionId: string | undefined, enabled = true) {
  return useQuery({ queryKey: aiKeys.transcript(sessionId ?? ""), queryFn: () => aiApi.transcript(sessionId!), enabled: !!sessionId && enabled, staleTime: Infinity, retry: false });
}

/** Resolve a citation to a session-relative time (ms). */
export function resolveCitation(c: Citation, transcript: Transcript | undefined, startTs?: number): number | null {
  if (typeof c.tStart === "number") {
    // Absolute epoch ms when a start timestamp is known, else already relative.
    return startTs && c.tStart > startTs ? c.tStart - startTs : c.tStart;
  }
  const step = transcript?.steps.find((s) => s.id === c.stepId);
  return step ? step.t : null;
}

/** Short label for a citation ("0:42" when resolvable, else the step id). */
export function citationLabel(c: Citation, transcript: Transcript | undefined, fmt: (ms: number) => string, startTs?: number): string {
  const t = resolveCitation(c, transcript, startTs);
  return t === null ? c.stepId : fmt(t);
}

/** Returns a seek function for citations bound to a session. */
export function useCitationSeek(sessionId: string | undefined, seek: (relMs: number) => void, startTs?: number) {
  const transcript = useTranscript(sessionId);
  return useMemo(
    () => ({
      transcript: transcript.data,
      seekTo: (c: Citation) => {
        const t = resolveCitation(c, transcript.data, startTs);
        if (t !== null) seek(t);
      },
      label: (c: Citation, fmt: (ms: number) => string) => citationLabel(c, transcript.data, fmt, startTs),
    }),
    [transcript.data, seek, startTs],
  );
}

// ---------------------------------------------------------------------------
// Feedback
// ---------------------------------------------------------------------------

export function useFeedback() {
  return useMutation({
    // The artifact already renders the selected thumb locally. Refetching every
    // AI query after a rating does unrelated work and can disturb an analysis
    // in progress, so persistence is the only responsibility of this mutation.
    mutationFn: ({ meta, rating, reason }: { meta: ArtifactMeta; rating: "up" | "down" | ""; reason?: string }) =>
      aiApi.feedback(meta.id, rating, reason),
  });
}

/** Trigger a browser download for generated text (tests, repro steps). */
export function downloadText(filename: string, text: string, type = "text/plain"): void {
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
